import Form, { useForm } from 'antd/lib/form/Form'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSection from 'components/CIMSSection'
import CIMSSelect from 'components/CIMSSelect'
import { PanelFormItems, PanelProps } from './types'
import { useDevelopersModuleContext } from '../../../../../../DevelopersModuleContext'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { CIMSTags } from 'components/CIMSTags'
import { Button } from 'antd'
import { useEffect, useState } from 'react'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useUpsertUserToDeveloperPermissionsMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'

export const Panel: React.VFC<PanelProps> = ({ permission }) => {
  const { users, developers, refetch } = useDevelopersModuleContext()
  const [form] = useForm<PanelFormItems>()
  const [upserting, setUpserting] = useState(false)
  const [upsert] = useUpsertUserToDeveloperPermissionsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      upsertUserToDeveloperPermission: { userToDeveloperPermissions },
    }) =>
      userToDeveloperPermissions.map(({ permission, user: { username } }) =>
        simpleSuccessNotification(`${username} sucessfully given ${permission}`)
      ),
  })

  useEffect(() => {
    form.resetFields()
  }, [form, users, developers])

  const initialValues: PanelFormItems = {
    users: toLabeledValues(
      users.filter(({ userToDeveloperPermissions }) =>
        userToDeveloperPermissions.some(
          ({ permissionId }) => permissionId === permission.id
        )
      ),
      {
        value: 'id',
        label: 'username',
        tooltip: ({ username, mail }) => (username === mail ? undefined : mail),
      }
    ),
  }

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={async ({ users }) => {
        setUpserting(true)
        const permissionId = permission.id
        const userIds = users.map(({ value }) => value.toString())
        await upsert({ variables: { input: { permissionId, userIds } } })
        setUpserting(false)
        refetch()
      }}
    >
      <CIMSSection
        title="Developers"
        help="Developers have full access to the entire application"
      >
        <CIMSTags
          tags={developers.map(({ username, mail }) => ({
            text: username,
            tooltip: username === mail ? undefined : mail,
          }))}
        />
      </CIMSSection>
      <CIMSSection
        title="Additional Users"
        help="Other users explictly permitted to perform this action"
      >
        <CIMSFormItem
          label="Users"
          help="Any user who is not a developer"
          name="users"
        >
          <CIMSSelect
            options={useOptions({
              items: users.filter(({ developer }) => !developer),
              sort: 'username',
              value: 'id',
              label: 'username',
              tooltip: ({ username, mail }) =>
                username === mail ? undefined : mail,
            })}
            mode="multiple"
          />
        </CIMSFormItem>
        <Button
          loading={upserting}
          disabled={upserting}
          type="primary"
          htmlType="submit"
        >
          Save
        </Button>
      </CIMSSection>
    </Form>
  )
}

export default Panel
